import { craftSeconds, type Recipe } from '../ui/crafting';

// The craft QUEUE: one item crafts at a time (the head), the rest wait their turn -- Rust-style. The
// cost is spent when a job is queued (cancel refunds it); the item is granted when its timer completes.
// main.ts ticks update() each frame and routes finished jobs through the backend grant.

export interface CraftJob {
  recipe: Recipe;
  totalMs: number;
  endAt: number; // ms timestamp when this job completes; 0 = waiting behind the head
}

export class CraftQueue {
  private readonly jobs: CraftJob[] = [];
  onChange: (() => void) | null = null; // fired on enqueue/cancel/finish so the UI repaints

  list(): readonly CraftJob[] {
    return this.jobs;
  }

  get length(): number {
    return this.jobs.length;
  }

  enqueue(recipe: Recipe, now: number): void {
    const totalMs = craftSeconds(recipe) * 1000;
    this.jobs.push({ recipe, totalMs, endAt: this.jobs.length === 0 ? now + totalMs : 0 });
    this.onChange?.();
  }

  // Remove a job (the caller refunds its cost). Cancelling the head starts the next job immediately.
  cancel(index: number, now: number): Recipe | null {
    const job = this.jobs[index];
    if (!job) return null;
    this.jobs.splice(index, 1);
    if (index === 0 && this.jobs.length) this.jobs[0].endAt = now + this.jobs[0].totalMs;
    this.onChange?.();
    return job.recipe;
  }

  // Move a job one step up/down the queue (owner QoL). Moving into/out of the HEAD restarts the head's
  // timer from scratch (its progress belongs to the job that was crafting, not the slot).
  move(index: number, dir: -1 | 1, now: number): void {
    const j = index + dir;
    if (index < 0 || j < 0 || index >= this.jobs.length || j >= this.jobs.length) return;
    [this.jobs[index], this.jobs[j]] = [this.jobs[j], this.jobs[index]];
    if (index === 0 || j === 0) { this.jobs[0].endAt = now + this.jobs[0].totalMs; if (this.jobs[1]) this.jobs[1].endAt = 0; }
    this.onChange?.();
  }

  // Advance the head; returns every recipe that finished this tick (usually 0 or 1, more after a long hitch).
  update(now: number): Recipe[] {
    const done: Recipe[] = [];
    while (this.jobs.length && this.jobs[0].endAt > 0 && this.jobs[0].endAt <= now) {
      const fin = this.jobs.shift()!;
      done.push(fin.recipe);
      if (this.jobs.length) this.jobs[0].endAt = fin.endAt + this.jobs[0].totalMs; // chain, no drift
    }
    if (done.length) this.onChange?.();
    return done;
  }

  // Head progress 0..1 (for the progress bar); 0 for queued jobs.
  progress(index: number, now: number): number {
    const job = this.jobs[index];
    if (!job || job.endAt <= 0) return 0;
    return Math.max(0, Math.min(1, 1 - (job.endAt - now) / job.totalMs));
  }

  // Seconds left on a job (queued jobs report their full time).
  remainingSec(index: number, now: number): number {
    const job = this.jobs[index];
    if (!job) return 0;
    return Math.max(0, (job.endAt > 0 ? job.endAt - now : job.totalMs)) / 1000;
  }
}
